// Reads what EduQuery extracted from each slide out of `Lesson.eduquerySlides`.
//
// SHAPE, COUNTED ACROSS EVERY STORED LESSON rather than taken from the contract
// or generalised from one sample — all 325 production lessons and all 5 on
// staging:
//
//   PRODUCTION, all 325:  { "slides": { "page_1": {...}, "page_2": {...} } }
//   STAGING, 3 of 5:      same
//   STAGING, 2 of 5:      { "source_format": "pptx",
//                           "slides": { "0": { index, text, notes, tables,
//                                              links, complexity } } }
//
// The thing that broke an earlier version of this file is the NESTING: we store
// the whole upload response, not the inner dict the API contract describes, so
// a parser looking for TOP-LEVEL page_N matches nothing on any real lesson.
// That was the bug, and it is why the video-link feature never once fired.
//
// The digit-keyed, source_format-carrying form is NOT the normal shape. Those
// two staging rows are manual test artifacts from EduQuery v2, which is not
// connected to anything yet. An earlier revision of this comment called bare
// zero-based keys "the real shape" — that generalised from a single sample and
// was wrong. They are real and must keep working, but they are the exception.
//
// So: nested or flat, and page_N / slide_N / bare digits, all normalised to the
// 1-based page number the presenting view uses.

export interface VideoLink {
  url: string
  title?: string
}

const PREFIXED_KEY = /^(?:page|slide)_(\d+)$/
const BARE_KEY = /^\d+$/

/** Page number (1-based) for a slide key, or null if it isn't a slide key. */
const pageNumberFor = (key: string): number | null => {
  const prefixed = PREFIXED_KEY.exec(key)
  if (prefixed) return Number(prefixed[1])
  // Bare keys are zero-based indices: "0" is the first slide, i.e. page 1.
  if (BARE_KEY.test(key)) return Number(key) + 1
  return null
}

/** The slide map, wherever it lives in the stored blob. */
const slideMap = (eduquerySlides: string | null | undefined): Record<string, any> => {
  if (!eduquerySlides) return {}

  let parsed: unknown
  try {
    parsed = JSON.parse(eduquerySlides)
  } catch {
    return {}
  }

  if (!parsed || typeof parsed !== 'object') return {}

  const root = parsed as Record<string, unknown>
  const nested = root.slides
  if (nested && typeof nested === 'object') return nested as Record<string, any>
  return root as Record<string, any>
}

/* Only "embedded_video" links are surfaced. A plain "hyperlink" already renders
   as a clickable overlay via pdf.js's own annotation layer, so including it
   here would double a click target on the same spot. */
export const extractVideoLinksByPage = (
  eduquerySlides: string | null | undefined
): Record<number, VideoLink[]> => {
  const slides = slideMap(eduquerySlides)
  const result: Record<number, VideoLink[]> = {}

  for (const [key, slide] of Object.entries(slides)) {
    const page = pageNumberFor(key)
    if (page === null) continue

    const links = slide?.links
    if (!Array.isArray(links)) continue

    const videoLinks = links
      .filter((link): link is { url: string; kind: string; title?: string } =>
        !!link && typeof link === 'object' && link.kind === 'embedded_video' && typeof link.url === 'string'
      )
      .map(link => ({
        url: link.url,
        title: typeof link.title === 'string' ? link.title : undefined,
      }))

    if (videoLinks.length) result[page] = videoLinks
  }

  return result
}

/* The text EduQuery extracted from each slide, for read-aloud on the teacher's
   board. It is the only text available there: the board renders the uploaded
   slide as a PDF page IMAGE, so there is nothing on screen to read. What comes
   back is an extraction, not authored copy -- on a picture-heavy slide it can
   be empty or oddly ordered, which is why callers check for text before
   offering the control. Presenter notes are appended when present: a teacher
   asking to hear the slide wants what they wrote for themselves too. */
export const extractSlideTextByPage = (
  eduquerySlides: string | null | undefined
): Record<number, string> => {
  const slides = slideMap(eduquerySlides)
  const result: Record<number, string> = {}

  for (const [key, slide] of Object.entries(slides)) {
    const page = pageNumberFor(key)
    if (page === null) continue

    const parts: string[] = []
    if (typeof slide?.text === 'string' && slide.text.trim()) parts.push(slide.text.trim())
    if (typeof slide?.notes === 'string' && slide.notes.trim()) parts.push(slide.notes.trim())

    if (parts.length) result[page] = parts.join('. ')
  }

  return result
}
